import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { type ThemeProviderProps } from 'next-themes/dist/types';
import { useAppStore } from '@/stores';
import { tokens, type Theme } from '@/lib/design-system';

// Enhanced theme context with design system integration
interface EnhancedThemeContextValue {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  systemTheme: 'light' | 'dark';
  colors: typeof tokens.colors;
  spacing: typeof tokens.spacing;
  borderRadius: typeof tokens.borderRadius;
  fontSize: typeof tokens.fontSize;
  fontWeight: typeof tokens.fontWeight;
  shadows: typeof tokens.shadows;
  transitions: typeof tokens.transitions;
  zIndex: typeof tokens.zIndex;
  getCSSVariable: (path: string, fallback?: string) => string;
  setCSSVariable: (path: string, value: string) => void;
}

const EnhancedThemeContext = createContext<EnhancedThemeContextValue | undefined>(undefined);

export function useEnhancedTheme() {
  const context = useContext(EnhancedThemeContext);
  if (context === undefined) {
    throw new Error('useEnhancedTheme must be used within an EnhancedThemeProvider');
  }
  return context;
}

interface EnhancedThemeProviderProps extends ThemeProviderProps {
  children: React.ReactNode;
}

export function EnhancedThemeProvider({ children, ...props }: EnhancedThemeProviderProps) {
  const { theme: appTheme, setTheme: setAppTheme } = useAppStore();
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>('light');
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');

  // Detect system theme preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Resolve theme based on app theme and system preference
  useEffect(() => {
    if (appTheme === 'system') {
      setResolvedTheme(systemTheme);
    } else {
      setResolvedTheme(appTheme as 'light' | 'dark');
    }
  }, [appTheme, systemTheme]);

  // Set CSS variables for design tokens
  useEffect(() => {
    const root = document.documentElement;

    // Set color scheme
    root.style.setProperty('color-scheme', resolvedTheme);

    // Apply theme-aware colors
    const colors = resolvedTheme === 'dark' ? darkColors : tokens.colors;

    // Apply primary colors
    Object.entries(colors.primary).forEach(([key, value]) => {
      root.style.setProperty(`--color-primary-${key}`, value);
    });

    // Apply secondary colors
    Object.entries(colors.secondary).forEach(([key, value]) => {
      root.style.setProperty(`--color-secondary-${key}`, value);
    });

    // Apply semantic colors
    Object.entries(colors.success).forEach(([key, value]) => {
      root.style.setProperty(`--color-success-${key}`, value);
    });

    Object.entries(colors.warning).forEach(([key, value]) => {
      root.style.setProperty(`--color-warning-${key}`, value);
    });

    Object.entries(colors.error).forEach(([key, value]) => {
      root.style.setProperty(`--color-error-${key}`, value);
    });

    // Apply spacing tokens
    Object.entries(tokens.spacing).forEach(([key, value]) => {
      root.style.setProperty(`--spacing-${key}`, value);
    });

    // Apply border radius tokens
    Object.entries(tokens.borderRadius).forEach(([key, value]) => {
      root.style.setProperty(`--radius-${key}`, value);
    });

    // Apply font size tokens
    Object.entries(tokens.fontSize).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        root.style.setProperty(`--font-size-${key}`, value[0]);
        root.style.setProperty(`--line-height-${key}`, value[1] as string);
      }
    });

    // Apply font weight tokens
    Object.entries(tokens.fontWeight).forEach(([key, value]) => {
      root.style.setProperty(`--font-weight-${key}`, value.toString());
    });

    // Apply shadow tokens
    Object.entries(tokens.shadows).forEach(([key, value]) => {
      root.style.setProperty(`--shadow-${key}`, value);
    });

    // Apply transition tokens
    Object.entries(tokens.transitions).forEach(([key, value]) => {
      root.style.setProperty(`--transition-${key}`, value);
    });

    // Apply z-index tokens
    Object.entries(tokens.zIndex).forEach(([key, value]) => {
      root.style.setProperty(`--z-${key}`, value.toString());
    });

    // Apply theme-specific background and foreground colors
    if (resolvedTheme === 'dark') {
      root.style.setProperty('--background', '0 0% 3.9%');
      root.style.setProperty('--foreground', '0 0% 98%');
      root.style.setProperty('--card', '0 0% 3.9%');
      root.style.setProperty('--card-foreground', '0 0% 98%');
      root.style.setProperty('--popover', '0 0% 3.9%');
      root.style.setProperty('--popover-foreground', '0 0% 98%');
      root.style.setProperty('--primary', '0 0% 98%');
      root.style.setProperty('--primary-foreground', '0 0% 9%');
      root.style.setProperty('--secondary', '0 0% 14.9%');
      root.style.setProperty('--secondary-foreground', '0 0% 98%');
      root.style.setProperty('--muted', '0 0% 14.9%');
      root.style.setProperty('--muted-foreground', '0 0% 63.9%');
      root.style.setProperty('--accent', '0 0% 14.9%');
      root.style.setProperty('--accent-foreground', '0 0% 98%');
      root.style.setProperty('--destructive', '0 62.8% 30.6%');
      root.style.setProperty('--destructive-foreground', '0 0% 98%');
      root.style.setProperty('--border', '0 0% 14.9%');
      root.style.setProperty('--input', '0 0% 14.9%');
      root.style.setProperty('--ring', '0 0% 83.1%');
    } else {
      root.style.setProperty('--background', '0 0% 100%');
      root.style.setProperty('--foreground', '0 0% 3.9%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '0 0% 3.9%');
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', '0 0% 3.9%');
      root.style.setProperty('--primary', '0 0% 9%');
      root.style.setProperty('--primary-foreground', '0 0% 98%');
      root.style.setProperty('--secondary', '0 0% 96.1%');
      root.style.setProperty('--secondary-foreground', '0 0% 9%');
      root.style.setProperty('--muted', '0 0% 96.1%');
      root.style.setProperty('--muted-foreground', '0 0% 45.1%');
      root.style.setProperty('--accent', '0 0% 96.1%');
      root.style.setProperty('--accent-foreground', '0 0% 9%');
      root.style.setProperty('--destructive', '0 84.2% 60.2%');
      root.style.setProperty('--destructive-foreground', '0 0% 98%');
      root.style.setProperty('--border', '0 0% 89.8%');
      root.style.setProperty('--input', '0 0% 89.8%');
      root.style.setProperty('--ring', '0 0% 3.9%');
    }

  }, [resolvedTheme]);

  const handleThemeChange = (theme: Theme) => {
    setAppTheme(theme);
  };

  const getCSSVariable = (path: string, fallback = ''): string => {
    return getComputedStyle(document.documentElement).getPropertyValue(`--${path}`).trim() || fallback;
  };

  const setCSSVariable = (path: string, value: string) => {
    document.documentElement.style.setProperty(`--${path}`, value);
  };

  const value: EnhancedThemeContextValue = {
    theme: appTheme as Theme,
    resolvedTheme,
    setTheme: handleThemeChange,
    systemTheme,
    colors: tokens.colors,
    spacing: tokens.spacing,
    borderRadius: tokens.borderRadius,
    fontSize: tokens.fontSize,
    fontWeight: tokens.fontWeight,
    shadows: tokens.shadows,
    transitions: tokens.transitions,
    zIndex: tokens.zIndex,
    getCSSVariable,
    setCSSVariable,
  };

  return (
    <EnhancedThemeContext.Provider value={value}>
      <NextThemesProvider {...props}>
        {children}
      </NextThemesProvider>
    </EnhancedThemeContext.Provider>
  );
}

// Dark theme color palette
const darkColors = {
  primary: {
    50: '#1e3a8a',
    100: '#1e40af',
    200: '#1d4ed8',
    300: '#2563eb',
    400: '#3b82f6',
    500: '#60a5fa',
    600: '#93c5fd',
    700: '#bfdbfe',
    800: '#dbeafe',
    900: '#eff6ff',
    950: '#f0f9ff',
  },
  secondary: {
    50: '#0f172a',
    100: '#1e293b',
    200: '#334155',
    300: '#475569',
    400: '#64748b',
    500: '#94a3b8',
    600: '#cbd5e1',
    700: '#e2e8f0',
    800: '#f1f5f9',
    900: '#f8fafc',
    950: '#f9fafb',
  },
  success: {
    50: '#052e16',
    100: '#14532d',
    200: '#166534',
    300: '#15803d',
    400: '#16a34a',
    500: '#22c55e',
    600: '#4ade80',
    700: '#86efac',
    800: '#bbf7d0',
    900: '#dcfce7',
    950: '#f0fdf4',
  },
  warning: {
    50: '#451a03',
    100: '#78350f',
    200: '#92400e',
    300: '#b45309',
    400: '#d97706',
    500: '#f59e0b',
    600: '#fbbf24',
    700: '#fcd34d',
    800: '#fde68a',
    900: '#fef3c7',
    950: '#fffbeb',
  },
  error: {
    50: '#450a0a',
    100: '#7f1d1d',
    200: '#991b1b',
    300: '#b91c1c',
    400: '#dc2626',
    500: '#ef4444',
    600: '#f87171',
    700: '#fca5a5',
    800: '#fecaca',
    900: '#fee2e2',
    950: '#fef2f2',
  },
};